import { FC, ReactNode, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Nullable } from '../types/common';
import dayjs, { Dayjs } from 'dayjs';
import dayJsDuration from 'dayjs/plugin/duration';
import dayJsTimezone from 'dayjs/plugin/timezone';
import dayJsUtc from 'dayjs/plugin/utc';

dayjs.extend(dayJsDuration);
dayjs.extend(dayJsTimezone);
dayjs.extend(dayJsUtc);

export const TIME_FORMAT = 'HH:mm';
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_INTL_FORMAT = 'YYYY-MM-DD HH:mm';
export const DATE_TIME_INTL_SECONDS_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DATE_TIME_ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';

export const formatDate = (
	date: string | Date | dayjs.Dayjs,
	timezone?: Nullable<string>,
	format: string = DATE_TIME_INTL_SECONDS_FORMAT
): string => {
	if (timezone?.length) {
		const tzDate = dayjs(date).tz(timezone).format(format);
		return `${tzDate} (${timezone})`;
	}
	// default
	return dayjs.tz(date).format(format);
};

export const getDuration = (end_date: string | Dayjs, start_date: string | Dayjs): dayJsDuration.Duration => {
	return dayjs.duration(dayjs(end_date).diff(dayjs(start_date)));
};

// ! Time Diff
interface IDisplayDuration {
	duration?: plugin.Duration;
	placeholder?: ReactNode;
}

interface IDisplayDiffTimeProps extends Omit<IDisplayDuration, 'duration'> {
	startDate: undefined | string | Date | dayjs.Dayjs;
	endDate: undefined | string | Date | dayjs.Dayjs;
	type: 'short' | 'long';
}

export const DisplayDiffTime: FC<IDisplayDiffTimeProps> = ({ startDate, endDate, placeholder, type = 'long' }) => {
	if (!startDate || !endDate) {
		return placeholder ? <>{placeholder}</> : null;
	}

	const duration = dayjs.duration(dayjs(endDate).diff(dayjs(startDate)));

	if (type === 'short') return <ShortDurationDisplay duration={duration} />;

	return <DurationDisplay duration={duration} />;
};

export const ShortDurationDisplay: FC<IDisplayDuration> = memo(({ duration, placeholder }) => {
	if (!duration) {
		return placeholder ? <>{placeholder}</> : null;
	}

	const units = [
		{ label: 'y', value: duration.years() },
		{ label: 'm', value: duration.months() },
		{ label: 'd', value: duration.days() },
		{ label: 'h', value: duration.hours() },
		{ label: 'min', value: duration.minutes() },
		{ label: 'second', value: duration.seconds() },
	];

	const parts = units.filter(({ value }) => value > 0);

	const firstElement = parts.at(0);

	// * if only seconds left
	if (parts.length === 1 && firstElement?.label === 'second') {
		return <>a few seconds</>;
	}

	return (
		<>
			{parts
				.filter(({ label }) => label !== 'second')
				.map(({ label, value }) => [value, label])
				.flatMap((arr) => arr)
				.join(' ') + ' 	ago'}
		</>
	);
});

export const DurationDisplay: FC<IDisplayDuration> = memo(({ duration, placeholder }) => {
	const { t: tCommon } = useTranslation('common');

	if (!duration) {
		return placeholder ? <>{placeholder}</> : null;
	}

	const units = [
		{ key: 'year', value: duration.years() },
		{ key: 'month', value: duration.months() },
		{ key: 'day', value: duration.days() },
		{ key: 'hour', value: duration.hours() },
		{ key: 'minute', value: duration.minutes() },
		{ key: 'second', value: duration.seconds() },
	];

	const parts = units
		.filter(({ value }) => value > 0)
		.map(({ key, value }) => [value, tCommon(`time_units.${key}`, { count: value })].join(' '));

	return <span> {parts.join(' ')} </span>;
});
